(() => {
  const selector = '[data-gallery-thumb][aria-selected="true"]';
  const reduceMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const centerSelectedThumb = (rail, behavior = null) => {
    const thumb = rail.querySelector(selector);
    if (!thumb) return;

    const scrollBehavior = behavior || (reduceMotion() ? 'auto' : 'smooth');
    const railRect = rail.getBoundingClientRect();
    const thumbRect = thumb.getBoundingClientRect();

    if (rail.scrollWidth > rail.clientWidth + 1) {
      const maxLeft = Math.max(0, rail.scrollWidth - rail.clientWidth);
      const centeredLeft = rail.scrollLeft
        + thumbRect.left
        - railRect.left
        - ((rail.clientWidth - thumbRect.width) / 2);
      rail.scrollTo({
        left: Math.max(0, Math.min(maxLeft, centeredLeft)),
        behavior: scrollBehavior
      });
      return;
    }

    if (rail.scrollHeight > rail.clientHeight + 1) {
      const maxTop = Math.max(0, rail.scrollHeight - rail.clientHeight);
      const centeredTop = rail.scrollTop
        + thumbRect.top
        - railRect.top
        - ((rail.clientHeight - thumbRect.height) / 2);
      rail.scrollTo({
        top: Math.max(0, Math.min(maxTop, centeredTop)),
        behavior: scrollBehavior
      });
    }
  };

  const init = (root) => {
    if (!root || root.dataset.galleryThumbSync === 'true') return;
    const rail = root.querySelector('.pdp__thumbs');
    if (!rail) return;
    const dots = [...root.querySelectorAll('[data-gallery-dot]')];
    const stage = root.querySelector('[data-gallery-stage]');

    root.dataset.galleryThumbSync = 'true';
    let frame = 0;
    let settleTimers = [];
    const controller = new AbortController();
    const queueSync = (behavior = null) => {
      window.cancelAnimationFrame(frame);
      frame = window.requestAnimationFrame(() => centerSelectedThumb(rail, behavior));
    };
    const queueSettledSync = () => {
      settleTimers.forEach((timer) => window.clearTimeout(timer));
      queueSync();
      settleTimers = [
        window.setTimeout(() => queueSync(), 180),
        window.setTimeout(() => queueSync('auto'), 700)
      ];
    };
    const syncDots = () => {
      const activeIndex = rail.querySelector(selector)?.dataset.mediaIndex;
      dots.forEach((dot) => {
        dot.setAttribute('aria-current', String(dot.dataset.mediaIndex === activeIndex));
      });
    };

    const observer = new MutationObserver((mutations) => {
      if (mutations.some((mutation) => mutation.target.matches?.(selector))) {
        syncDots();
        queueSettledSync();
      }
    });
    observer.observe(rail, {
      subtree: true,
      attributes: true,
      attributeFilter: ['aria-selected']
    });

    window.addEventListener('resize', () => queueSync('auto'), {
      passive: true,
      signal: controller.signal
    });
    stage?.addEventListener('scroll', queueSettledSync, {
      passive: true,
      signal: controller.signal
    });
    let swipeStart = null;
    let suppressClickUntil = 0;
    stage?.addEventListener('pointerdown', (event) => {
      if (!window.matchMedia('(max-width: 989px)').matches) return;
      if (!['touch', 'pen'].includes(event.pointerType)) return;
      if (event.target.closest('video,model-viewer,iframe')) return;
      swipeStart = {
        id: event.pointerId,
        x: event.clientX,
        y: event.clientY
      };
      stage.classList.add('is-swiping');
      try { stage.setPointerCapture(event.pointerId); } catch (_) {}
    }, { signal: controller.signal });
    stage?.addEventListener('pointerup', (event) => {
      if (!swipeStart || event.pointerId !== swipeStart.id) return;
      const distanceX = event.clientX - swipeStart.x;
      const distanceY = event.clientY - swipeStart.y;
      swipeStart = null;
      stage.classList.remove('is-swiping');
      try { stage.releasePointerCapture(event.pointerId); } catch (_) {}
      if (Math.abs(distanceX) < 44 || Math.abs(distanceX) <= Math.abs(distanceY) * 1.15) return;
      root.querySelector(distanceX < 0 ? '[data-gallery-next]' : '[data-gallery-prev]')?.click();
      suppressClickUntil = window.performance.now() + 400;
    }, { signal: controller.signal });
    stage?.addEventListener('pointercancel', () => {
      swipeStart = null;
      stage.classList.remove('is-swiping');
    }, { signal: controller.signal });
    stage?.addEventListener('click', (event) => {
      if (window.performance.now() >= suppressClickUntil) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      suppressClickUntil = 0;
    }, { capture: true, signal: controller.signal });
    root.addEventListener('click', (event) => {
      const dot = event.target.closest('[data-gallery-dot]');
      if (dot) {
        rail.querySelector(`[data-gallery-thumb][data-media-index="${dot.dataset.mediaIndex}"]`)?.click();
        return;
      }
      if (event.target.closest('[data-gallery-prev],[data-gallery-next],[data-gallery-thumb]')) {
        queueSettledSync();
      }
    }, { signal: controller.signal });
    root.addEventListener('keydown', (event) => {
      const dot = event.target.closest('[data-gallery-dot]');
      if (!dot || !['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
      event.preventDefault();
      const index = dots.indexOf(dot);
      const targetIndex = event.key === 'Home'
        ? 0
        : event.key === 'End'
          ? dots.length - 1
          : (index + (event.key === 'ArrowRight' ? 1 : -1) + dots.length) % dots.length;
      dots[targetIndex]?.click();
      dots[targetIndex]?.focus({ preventScroll: true });
    }, { signal: controller.signal });

    root.urbGalleryThumbSync = {
      destroy: () => {
        observer.disconnect();
        controller.abort();
        window.cancelAnimationFrame(frame);
        settleTimers.forEach((timer) => window.clearTimeout(timer));
      }
    };
    syncDots();
    queueSync('auto');
  };

  const boot = (scope = document) => {
    scope.querySelectorAll?.('[data-product-page]').forEach(init);
    if (scope.matches?.('[data-product-page]')) init(scope);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => boot(), { once: true });
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', (event) => boot(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    const root = event.target.matches?.('[data-product-page]')
      ? event.target
      : event.target.querySelector?.('[data-product-page]');
    root?.urbGalleryThumbSync?.destroy();
  });
})();
